// components/kyc/OtherTermsAcceptanceDialog.tsx
//
// Dialog for accepting other terms (seller, buyer consumer, buyer B2B, pricing)
// This must be shown BEFORE ID verification can be initiated

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { FileText } from "lucide-react";
import TermsDialog from "@/components/footer/terms_dialog";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { UserType } from "@/types/user";
import { useLanguage } from "@/context/LanguageContext";
import { commonTermsAcceptanceLabels } from "@/i18n";

interface OtherTermsAcceptanceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAccepted: () => void; // Called when all terms are accepted
}

const OtherTermsAcceptanceDialog = ({
  isOpen,
  onClose,
  onAccepted,
}: OtherTermsAcceptanceDialogProps) => {
  const { user, acceptOtherTermsAsync, isAcceptingOtherTerms } = useAuth();
  const { language } = useLanguage();
  const taLabels = commonTermsAcceptanceLabels(language);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);

  // Determine which buyer terms to show based on user type
  const isIndividual = user?.type === UserType.Individual;
  const isBusinessOrDealer =
    user?.type === UserType.Business || user?.type === UserType.Dealer;

  const visibleTabs = (isIndividual
    ? (["buyer-consumer", "seller-consumer", "complaint", "pricing"] as const)
    : (["buyer-b2b", "seller-business", "complaint", "pricing"] as const));

  const handleContinue = async () => {
    if (!termsAccepted) {
      toast.error(taLabels.toastMustApproveAll || "You must approve all terms to continue.");
      return;
    }

    try {
      await acceptOtherTermsAsync();
      toast.success(taLabels.toastAccepted || "Terms accepted!");
      onAccepted();
      onClose();
    } catch (error: any) {
      const message =
        error?.message || taLabels.toastError || "An error occurred while accepting the terms";
      toast.error(message);
    }
  };

  const openTermsDialog = () => {
    setShowTermsDialog(true);
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#D4AF37]" />
              Godkännande av villkor krävs
            </DialogTitle>
            <DialogDescription>
              Innan du kan verifiera din identitet måste du godkänna följande
              villkor:
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="flex items-start gap-3">
              <Checkbox
                id="other-terms"
                checked={termsAccepted}
                onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                className="mt-1"
              />
              <label
                htmlFor="other-terms"
                className="text-sm cursor-pointer flex-1 leading-relaxed"
              >
                {taLabels.iApproveTheFollowing || "Jag godkänner följande villkor:"}{" "}
                <button
                  type="button"
                  onClick={openTermsDialog}
                  className="text-blue-600 dark:text-blue-400 font-bold hover:underline"
                >
                  {taLabels.termsAndPolicies || "Villkor & policys"}
                </button>{" "}
                <span className="text-muted-foreground text-xs">
                  {taLabels.termsAndPoliciesHint ||
                    "(Klicka för att se mer – OBS läs igenom respektive villkor/policy-flik)"}
                </span>{" "}
                <span className="text-red-500">*</span>
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isAcceptingOtherTerms}
            >
              {taLabels.cancel || "Cancel"}
            </Button>
            <Button
              onClick={handleContinue}
              disabled={!termsAccepted || isAcceptingOtherTerms}
              className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white"
            >
              {isAcceptingOtherTerms ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                  {taLabels.accepting || "Accepting..."}
                </div>
              ) : (
                taLabels.continueToVerification || "Continue to identity verification"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Terms Dialog - opens to the specific tab */}
      <TermsDialog
        open={showTermsDialog}
        onOpenChange={setShowTermsDialog}
        initialTab={visibleTabs[0]}
        visibleTabs={visibleTabs as any}
        highlightTabs={visibleTabs as any}
      />
    </>
  );
};

export default OtherTermsAcceptanceDialog;
